<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { router, useForm } from "@inertiajs/vue3";
import BrandButton from "@/Components/Brand/BrandButton.vue";
// Stores
import { useModalogStore } from "@/stores/modalog";

const modalogStore = useModalogStore();

// Props
const props = defineProps({
    currencyCode: {
        type: String,
        default: null,
        required: true,
    },
    currencySymbol: {
        type: String,
        default: null,
        required: true,
    },
});

// State
const isUpdated = ref(false);

// Hooks
const form = useForm({
    preferred_currency: props.currencyCode,
});

watch(
    () => props.currencyCode,
    (newValue, oldValue) => {
        if (newValue !== oldValue) {
            form.preferred_currency = newValue;
        }
    },
);

onMounted(() => {
    // On initial load, don't show the default currency inline modalog.
    // The inline modalog can be displayed when setting the initial state of the currency select dropdown
    // based on the user's preferred currency.
    nextTick(() => {
        modalogStore.hasInlineModalog = false;
    });
});

// Computed
const titleText = computed(() => {
    return isUpdated.value
        ? `${props.currencySymbol}${props.currencyCode} is saved as your default currency.`
        : `Set your default currency to ${props.currencySymbol}${props.currencyCode}?`;
});

// Methods
function cancel() {
    modalogStore.hasInlineModalog = false;
    // Reset the component to initial state when closing.
    nextTick(() => {
        isUpdated.value = false;
    });
}

function updatePreferences() {
    form.post(route("preferences.update"), {
        onSuccess: () => {
            isUpdated.value = true;
            setTimeout(() => {
                modalogStore.hasInlineModalog = false;
            }, 3000);
            router.reload({ only: ["auth.user"] });
        },
        onError: (errors) => {
            console.error(errors);
            modalogStore.hasInlineModalog = false;
        },
    });
}
</script>

<template>
    <!-- Inline card (not an overlay): rides inside the item edit form,
         gated by hasInlineModalog from the parent. -->
    <div
        class="relative rounded-[5px] border border-solid border-[#e6dfdd] bg-brand-cream/50 p-4 font-brand"
    >
        <button
            type="button"
            class="absolute right-3 top-3 grid size-6 place-items-center text-brand-navy transition hover:text-brand-red"
            aria-label="Close"
            @click="cancel"
        >
            <svg
                viewBox="0 0 19 19"
                class="size-3"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                aria-hidden="true"
            >
                <path d="M2 2l15 15M17 2L2 17" />
            </svg>
        </button>

        <p class="pr-8 text-[14px] font-bold leading-snug text-brand-navy">
            {{ titleText }}
        </p>
        <p class="mt-1.5">
            <a
                rel="noopener noreferrer"
                target="_blank"
                href="https://support.gifthero.com/page/adding-gifts/"
                class="text-[13px] font-semibold text-brand-navy underline underline-offset-2 transition hover:text-brand-red"
                >Learn about Default Currency</a
            >
        </p>

        <div v-if="!isUpdated" class="mt-3 flex items-center justify-end gap-4">
            <button
                type="button"
                class="text-[14px] font-bold text-brand-navy underline underline-offset-2 transition hover:text-brand-red disabled:pointer-events-none disabled:opacity-40"
                :disabled="form.processing"
                @click="cancel"
            >
                No
            </button>
            <BrandButton
                class="h-9 px-5 text-[14px]"
                :loading="form.processing"
                @click="updatePreferences"
            >
                {{ form.processing ? "Saving" : "Yes" }}
            </BrandButton>
        </div>
    </div>
</template>
